import { API_ORIGIN } from '@/constants'
import { allauthJsonHeaders } from '@/lib/allauth-csrf'
import { completeSocialAuth, type SocialCompletePayload } from '@/lib/social-auth'

type AllauthPayload = {
  status?: number
  meta?: { is_authenticated?: boolean }
  errors?: Array<{ message?: string; code?: string }>
}

function extractError(payload: AllauthPayload): string | null {
  const errors = payload.errors
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0]
    if (first.message) return first.message
  }
  return null
}

function hasPendingLoginByCode(payload: AllauthPayload): boolean {
  const flows = (payload as { data?: { flows?: Array<{ id: string; is_pending?: boolean }> } }).data?.flows ?? []
  return flows.some((flow) => flow.id === 'login_by_code' && flow.is_pending)
}

/** Request a one-time sign-in code (Allauth "login by code"). */
export async function requestLoginCode(email: string): Promise<{ success: boolean; error?: string }> {
  const headers = await allauthJsonHeaders()
  const response = await fetch(`${API_ORIGIN}/_allauth/browser/v1/auth/code/request`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ email }),
  })

  const payload = (await response.json().catch(() => ({}))) as AllauthPayload

  // 401 with pending login_by_code flow means the email step succeeded.
  if (response.status === 401 && hasPendingLoginByCode(payload)) {
    return { success: true }
  }

  if (response.ok || payload.meta?.is_authenticated) {
    return { success: true }
  }

  return {
    success: false,
    error: extractError(payload) ?? 'Could not send sign-in code',
  }
}

/** Confirm code and exchange session for JWT via accounts API. */
export async function confirmLoginCode(code: string): Promise<SocialCompletePayload> {
  const headers = await allauthJsonHeaders()
  const response = await fetch(`${API_ORIGIN}/_allauth/browser/v1/auth/code/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ code: code.trim() }),
  })

  const payload = (await response.json().catch(() => ({}))) as AllauthPayload

  if (!response.ok && !payload.meta?.is_authenticated) {
    return {
      success: false,
      error: extractError(payload) ?? 'Invalid or expired code',
    }
  }

  return completeSocialAuth()
}
